import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Button, Card } from './common';
import FiveWhyAnalysisTool from './FiveWhyAnalysisTool';
import FishboneDiagramTool from './FishboneDiagramTool';
import ChartGeneratorTool from './ChartGeneratorTool';
import RootCauseClassification from './RootCauseClassification';
import Overview from './Overview';
import { useStyles } from '../contexts/StyleContext';

const Tab = ({ children, isActive, onClick }) => {
  const styles = useStyles();
  const { navigation } = styles;

  return (
    <button
      className={navigation.tab.base}
      style={isActive ? navigation.tab.active : {
        ...navigation.tab.inactive,
        ':hover': navigation.tab.inactive.hover
      }}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const CausalFactorsTable = ({ title, factors }) => {
  const styles = useStyles();
  const { table, card } = styles;

  return (
    <Card className={card.container.base} style={{ borderColor: card.container.borderColor }}>
      <h3 className={card.header.base} style={{ color: card.header.color }}>
        {title}
      </h3>
      <div className="overflow-x-auto">
        <table className={table.container.base}>
          <thead>
            <tr style={{ backgroundColor: table.header.backgroundColor }}>
              <th className={table.header.base} 
                  style={{ color: table.header.color, borderColor: table.header.borderColor }}>
                Type
              </th>
              <th className={table.header.base}
                  style={{ color: table.header.color, borderColor: table.header.borderColor }}>
                Cause Description
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className={table.row.hover}>
              <td className={table.cell.base}
                  style={{ color: table.header.color, borderColor: table.cell.borderColor }}>
                Root Causes
              </td>
              <td className={table.cell.base} style={{ borderColor: table.cell.borderColor }}>
                {factors.root.length > 0 ? 
                  factors.root.join(', ') : 
                  <span style={{ color: table.cell.color }} className="italic">
                    No root causes identified
                  </span>
                }
              </td>
            </tr>
            <tr className={table.row.hover}>
              <td className={table.cell.base}
                  style={{ color: table.header.color, borderColor: table.cell.borderColor }}>
                Contributing Causes
              </td>
              <td className={table.cell.base} style={{ borderColor: table.cell.borderColor }}>
                {factors.contributing.length > 0 ? 
                  factors.contributing.join(', ') : 
                  <span style={{ color: table.cell.color }} className="italic">
                    No contributing causes identified
                  </span>
                }
              </td>
            </tr>
            <tr className={table.row.hover}>
              <td className={table.cell.base}
                  style={{ color: table.header.color }}>
                Possible Causes
              </td>
              <td className={table.cell.base}>
                {factors.possible.length > 0 ? 
                  factors.possible.join(', ') : 
                  <span style={{ color: table.cell.color }} className="italic">
                    No possible causes identified
                  </span>
                }
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const RootCauseIdentification = ({ projectId }) => {
  const styles = useStyles();
  const { header, layout, navigation, card } = styles;

  const [activeTab, setActiveTab] = useState('overview');
  const [problemStatement, setProblemStatement] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [fiveWhysCausalFactors, setFiveWhysCausalFactors] = useState(null);
  const [fishboneCausalFactors, setFishboneCausalFactors] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      setIsLoading(true);
      try {
        const { data: problemData } = await supabase
          .from('problem_statements')
          .select('full_statement')
          .eq('project_id', projectId)
          .maybeSingle();

        setProblemStatement(problemData?.full_statement || '');

        if (problemData?.full_statement) {
          // Fetch Five Whys data
          const { data: whysData } = await supabase
            .from('five_whys_analysis')
            .select('whys')
            .eq('project_id', projectId)
            .maybeSingle();
          
          if (whysData?.whys) {
            const factors = {
              root: [],
              contributing: [],
              possible: []
            };

            whysData.whys.forEach(why => {
              if (why.isRoot === 'Yes' || why.isRoot === true) {
                if (why.contributing) {
                  const contributingCauses = why.contributing.split(',')
                    .map(cause => cause.trim())
                    .filter(cause => cause.length > 0);
                  factors.contributing.push(...contributingCauses);
                }
                if (why.possible) {
                  const possibleCauses = why.possible.split(',')
                    .map(cause => cause.trim())
                    .filter(cause => cause.length > 0);
                  factors.possible.push(...possibleCauses);
                }
                if (why.because) {
                  factors.root.push(why.because.trim());
                }
              }
            });

            if (factors.root.length > 0 || factors.contributing.length > 0 || factors.possible.length > 0) {
              setFiveWhysCausalFactors(factors);
            }
          }

          // Fetch Fishbone data
          const { data: fishboneData } = await supabase
            .from('fishbone_diagrams')
            .select('data')
            .eq('project_id', projectId)
            .maybeSingle();
          
          if (fishboneData?.data?.cause_classifications) {
            const classifications = fishboneData.data.cause_classifications;
            if (classifications.root.length > 0 || 
                classifications.contributing.length > 0 || 
                classifications.possible.length > 0) {
              setFishboneCausalFactors(classifications);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <Overview />;
      case '5whys':
        return <FiveWhyAnalysisTool projectId={projectId} />;
      case 'fishbone':
        return <FishboneDiagramTool projectId={projectId} />;
      case 'charts':
        return <ChartGeneratorTool />;
      case 'classification':
        return (
          <div>
            {fiveWhysCausalFactors && (
              <CausalFactorsTable 
                title="Five Whys Analysis - Causal Factors" 
                factors={fiveWhysCausalFactors} 
              />
            )}
            {fishboneCausalFactors && (
              <CausalFactorsTable 
                title="Fishbone Diagram - Causal Factors" 
                factors={fishboneCausalFactors} 
              />
            )}
            <RootCauseClassification projectId={projectId} />
          </div>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return <div className={layout.page.base} style={{ color: card.content.color }}>Loading...</div>;
  }

  return (
    <div className={layout.section.base}>
      <h2 className={header.title.base} style={{ color: header.title.color }}>
        Root Cause Identification
      </h2>
      
      <Card className={card.container.base} style={{ borderColor: card.container.borderColor }}>
        <h3 className={header.subtitle.base} style={{ color: header.subtitle.color }}>
          Problem Statement:
        </h3>
        {problemStatement ? (
          <p className={card.content.base} style={{ color: card.content.color }}>
            {problemStatement}
          </p>
        ) : (
          <p className="font-medium" style={{ color: 'red' }}>
            Problem not defined. Please complete the Problem Definition step first.
          </p>
        )}
      </Card>

      {problemStatement && (
        <Card className="overflow-hidden">
          <nav className={navigation.container.base}>
            {['overview', '5whys', 'fishbone', 'charts', 'classification'].map((tab) => (
              <Tab
                key={tab}
                isActive={activeTab === tab}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'overview' ? 'Overview' : 
                 tab === '5whys' ? '5 Whys' :
                 tab === 'fishbone' ? 'Fishbone' :
                 tab === 'charts' ? 'Chart Generation' : 'Classification'}
              </Tab>
            ))}
          </nav>
          <div className="p-6">
            {renderTabContent()}
          </div>
        </Card>
      )}
    </div>
  );
};

export default RootCauseIdentification;
